import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ClientTimelineTab from '@/pages/clients/ClientTimelineTab';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Download,
  Eye,
  RefreshCw,
  Send,
  AlertTriangle,
  Calendar,
  User,
  Shield,
  TrendingUp,
  Loader2,
  ExternalLink,
  CheckCheck,
  X,
  FileSpreadsheet,
  Printer,
  Copy,
  Save,
  Pencil,
  ScanText,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type {
  IntakeClient,
  IntakeClientDocument,
  IntakeDocumentType,
  IntakeDocumentStatus,
  RiskLevel,
} from '@/types/intake';

interface IntakeClientDetailPageProps {
  editMode?: boolean;
}

export function IntakeClientDetailPage({ editMode = false }: IntakeClientDetailPageProps) {
  const { id, clientId } = useParams<{ id?: string; clientId?: string }>();
  const actualClientId = clientId || id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Edit form state
  const [isEditing, setIsEditing] = useState(editMode);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    abn: '',
    notes: '',
    riskLevel: '' as RiskLevel | '',
  });

  // State
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<IntakeClientDocument | null>(null);
  const [validationAction, setValidationAction] = useState<'approve' | 'reject'>('approve');
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch client details
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['intake-client', actualClientId],
    queryFn: () => intakeService.getClient(actualClientId!),
    enabled: !!actualClientId,
  });

  // Populate form data when client data is loaded
  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        phone: client.phone || '',
        company: client.company || '',
        abn: client.abn || '',
        notes: client.notes || '',
        riskLevel: client.riskLevel || '',
      });
    }
  }, [client]);

  // Update editing state when editMode prop changes
  useEffect(() => {
    setIsEditing(editMode);
  }, [editMode]);

  // Fetch client documents
  const { data: documents = [], isLoading: documentsLoading } = useQuery({
    queryKey: ['intake-client-documents', actualClientId],
    queryFn: () => intakeService.listClientDocuments({ clientId: actualClientId! }),
    enabled: !!actualClientId,
  });

  // Fetch ALL document types for reference (used for looking up document type names)
  const { data: documentTypesData } = useQuery({
    queryKey: ['intake-document-types'],
    queryFn: () => intakeService.listDocumentTypes({ isActive: true }),
  });

  // Fetch client's document requirements (which document types are assigned to this client)
  const { data: clientRequirements } = useQuery({
    queryKey: ['intake-client-document-requirements', actualClientId],
    queryFn: () => intakeService.getClientDocuments(actualClientId!),
    enabled: !!actualClientId,
  });

  // Fetch client progress
  const { data: progress } = useQuery({
    queryKey: ['intake-client-progress', actualClientId],
    queryFn: () => intakeService.getClientProgress(actualClientId!),
    enabled: !!actualClientId,
  });

  // Update client mutation
  const updateClientMutation = useMutation({
    mutationFn: (data: Partial<IntakeClient>) => intakeService.updateClient(actualClientId!, data),
    onSuccess: () => {
      toast.success('Client updated successfully');
      queryClient.invalidateQueries({ queryKey: ['intake-client', actualClientId] });
      queryClient.invalidateQueries({ queryKey: ['intake-clients'] });
      setIsEditing(false);
      // Navigate back to detail view if came from edit route
      if (editMode) {
        navigate(`/panel/intake/clients/${actualClientId}`);
      }
    },
    onError: () => {
      toast.error('Failed to update client');
    },
  });

  // Handle form submission
  const handleSave = () => {
    updateClientMutation.mutate({
      name: formData.name,
      phone: formData.phone || undefined,
      company: formData.company || undefined,
      abn: formData.abn || undefined,
      notes: formData.notes || undefined,
      riskLevel: formData.riskLevel || undefined,
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (client) {
      setFormData({
        name: client.name || '',
        phone: client.phone || '',
        company: client.company || '',
        abn: client.abn || '',
        notes: client.notes || '',
        riskLevel: client.riskLevel || '',
      });
    }
    setIsEditing(false);
    if (editMode) {
      navigate(`/panel/intake/clients/${actualClientId}`);
    }
  };

  // All document types from tenant (for looking up names)
  const allDocumentTypes = documentTypesData?.documentTypes || [];

  // Client's required document types (filtered based on what's assigned to this client)
  const clientRequiredDocTypeIds = new Set(
    (clientRequirements?.documents || []).map(d => d.documentTypeId)
  );

  // Filter document types to only those assigned to this client
  // If no requirements are set, fall back to all document types (for backward compatibility)
  const documentTypes = clientRequiredDocTypeIds.size > 0
    ? allDocumentTypes.filter(dt => clientRequiredDocTypeIds.has(dt.id))
    : allDocumentTypes;

  // Map of requirement to check if a document type is required for this client
  const clientRequirementMap = new Map(
    (clientRequirements?.documents || []).map(d => [d.documentTypeId, d.isRequired])
  );

  const isLoading = clientLoading || documentsLoading;

  // Validate document mutation
  const validateMutation = useMutation({
    mutationFn: (input: { documentId: string; action: 'approve' | 'reject'; adminNotes?: string; rejectionReason?: string }) =>
      intakeService.validateDocument(input),
    onSuccess: () => {
      toast.success(validationAction === 'approve' ? 'Document approved' : 'Document rejected');
      queryClient.invalidateQueries({ queryKey: ['intake-client-documents', actualClientId] });
      queryClient.invalidateQueries({ queryKey: ['intake-client-progress', actualClientId] });
      queryClient.invalidateQueries({ queryKey: ['intake-client', actualClientId] });
      setValidateDialogOpen(false);
      setSelectedDocument(null);
      setAdminNotes('');
      setRejectionReason('');
    },
    onError: () => {
      toast.error('Failed to validate document');
    },
  });

  // Resend email mutation
  const resendEmailMutation = useMutation({
    mutationFn: () => intakeService.resendWelcomeEmail(actualClientId!),
    onSuccess: () => {
      toast.success('Email sent successfully');
    },
  });

  // Download document state
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  // Extract document state
  const [extractingDocId, setExtractingDocId] = useState<string | null>(null);
  const [extractedDataDialogOpen, setExtractedDataDialogOpen] = useState(false);
  const [selectedExtractedData, setSelectedExtractedData] = useState<{
    documentId: string;
    documentName: string;
    extractedData: any;
    confidence: number | null;
  } | null>(null);
  const [isEditingExtractedData, setIsEditingExtractedData] = useState(false);
  const [editedFields, setEditedFields] = useState<Record<string, string>>({});
  const [savingExtractedData, setSavingExtractedData] = useState(false);

  // Handle document extraction
  const handleExtractDocument = async (documentId: string, documentName: string) => {
    setExtractingDocId(documentId);
    try {
      const result = await intakeService.extractDocument(documentId);
      toast.success('Document extraction completed');
      // Refresh the client data to get updated document status
      queryClient.invalidateQueries({ queryKey: ['intake-client', actualClientId] });
      // Show extracted data
      setSelectedExtractedData({
        documentId,
        documentName,
        extractedData: result.extractedData,
        confidence: result.confidence,
      });
      setExtractedDataDialogOpen(true);
    } catch (error: any) {
      console.error('Extraction failed:', error);
      toast.error(error.response?.data?.details || 'Failed to extract document');
    } finally {
      setExtractingDocId(null);
    }
  };

  // View existing extracted data - always fetch fresh from API
  const handleViewExtractedData = async (doc: IntakeClientDocument) => {
    try {
      const result = await intakeService.getExtractedData(doc.id);
      if (result.extractedData) {
        setSelectedExtractedData({
          documentId: doc.id,
          documentName: doc.originalName,
          extractedData: result.extractedData,
          confidence: result.confidence,
        });
        setExtractedDataDialogOpen(true);
      } else {
        toast.error('No extracted data available');
      }
    } catch (error) {
      toast.error('Failed to load extracted data');
    }
  };

  // Start editing extracted data
  const handleStartEditExtractedData = () => {
    if (selectedExtractedData?.extractedData?.fields) {
      // Initialize edited fields with current values (excluding rawText)
      const fields = { ...selectedExtractedData.extractedData.fields };
      delete fields.rawText; // Don't edit raw text
      setEditedFields(fields);
      setIsEditingExtractedData(true);
    } else if (selectedExtractedData?.extractedData?.keyValuePairs) {
      setEditedFields({ ...selectedExtractedData.extractedData.keyValuePairs });
      setIsEditingExtractedData(true);
    }
  };

  // Cancel editing
  const handleCancelEditExtractedData = () => {
    setIsEditingExtractedData(false);
    setEditedFields({});
  };

  // Save edited extracted data
  const handleSaveExtractedData = async () => {
    if (!selectedExtractedData) return;

    setSavingExtractedData(true);
    try {
      // Prepare the updated extracted data
      const updatedExtractedData = { ...selectedExtractedData.extractedData };
      if (updatedExtractedData.fields) {
        updatedExtractedData.fields = { ...updatedExtractedData.fields, ...editedFields };
      } else if (updatedExtractedData.keyValuePairs) {
        updatedExtractedData.keyValuePairs = { ...updatedExtractedData.keyValuePairs, ...editedFields };
      }

      await intakeService.updateExtractedData(selectedExtractedData.documentId, updatedExtractedData);

      // Update local state
      setSelectedExtractedData({
        ...selectedExtractedData,
        extractedData: updatedExtractedData,
      });

      setIsEditingExtractedData(false);
      setEditedFields({});
      toast.success('Extracted data updated successfully');

      // Refresh the client data
      queryClient.invalidateQueries({ queryKey: ['intake-client', actualClientId] });
    } catch (error: any) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.error || 'Failed to save extracted data');
    } finally {
      setSavingExtractedData(false);
    }
  };

  // Handle document download
  const handleDownloadDocument = async (documentId: string) => {
    setDownloadingDocId(documentId);
    try {
      const { downloadUrl } = await intakeService.getDocumentDownloadUrl(documentId);
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Update client status mutation
  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => intakeService.updateClient(actualClientId!, { status: status as any }),
    onSuccess: () => {
      toast.success('Client status updated');
      queryClient.invalidateQueries({ queryKey: ['intake-client', actualClientId] });
      queryClient.invalidateQueries({ queryKey: ['intake-clients'] });
    },
  });

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string; icon: React.ElementType }> = {
      pending: { label: 'Pending', className: 'bg-gray-500/10 text-gray-500', icon: Clock },
      active: { label: 'Active', className: 'bg-blue-500/10 text-blue-500', icon: RefreshCw },
      submitted: { label: 'Submitted', className: 'bg-purple-500/10 text-purple-500', icon: Send },
      under_review: { label: 'Under Review', className: 'bg-amber-500/10 text-amber-500', icon: Eye },
      approved: { label: 'Approved', className: 'bg-green-500/10 text-green-500', icon: CheckCircle2 },
      rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-500', icon: XCircle },
      archived: { label: 'Archived', className: 'bg-gray-500/10 text-gray-500', icon: FileText },
    };
    const { label, className, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={cn('gap-1', className)}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const getDocStatusBadge = (status: IntakeDocumentStatus) => {
    const config: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pending', className: 'bg-gray-500/10 text-gray-500' },
      uploaded: { label: 'Uploaded', className: 'bg-blue-500/10 text-blue-500' },
      processing: { label: 'Processing', className: 'bg-blue-500/10 text-blue-500' },
      extracted: { label: 'Extracted', className: 'bg-purple-500/10 text-purple-500' },
      validated: { label: 'Validated', className: 'bg-amber-500/10 text-amber-500' },
      approved: { label: 'Approved', className: 'bg-green-500/10 text-green-500' },
      rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-500' },
    };
    const { label, className } = config[status] || config.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const getRiskBadge = (level?: RiskLevel) => {
    if (!level) return null;
    const config: Record<string, { label: string; className: string }> = {
      low: { label: 'Low Risk', className: 'bg-green-500/10 text-green-500' },
      medium: { label: 'Medium Risk', className: 'bg-yellow-500/10 text-yellow-500' },
      high: { label: 'High Risk', className: 'bg-orange-500/10 text-orange-500' },
      critical: { label: 'Critical Risk', className: 'bg-red-500/10 text-red-500' },
    };
    const { label, className } = config[level] || config.low;
    return <Badge className={className}>{label}</Badge>;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get missing documents - only show document types that are:
  // 1. Assigned to this client (in documentTypes)
  // 2. Required for this client (from clientRequirementMap, or fall back to dt.isRequired)
  // 3. Not yet uploaded
  const uploadedDocTypeIds = new Set(documents.map(d => d.documentTypeId));
  const missingDocTypes = documentTypes.filter(dt => {
    // Check if this document type is required for this client
    const isRequiredForClient = clientRequirementMap.has(dt.id)
      ? clientRequirementMap.get(dt.id)
      : dt.isRequired;
    return isRequiredForClient && !uploadedDocTypeIds.has(dt.id);
  });

  // Get documents needing review
  const documentsNeedingReview = documents.filter(d =>
    ['uploaded', 'processing', 'extracted', 'validated'].includes(d.status)
  );

  // Calculate completion stats
  const approvedDocs = documents.filter(d => d.status === 'approved').length;
  const rejectedDocs = documents.filter(d => d.status === 'rejected').length;
  const requiredDocTypes = documentTypes.filter(dt => {
    const isRequiredForClient = clientRequirementMap.has(dt.id)
      ? clientRequirementMap.get(dt.id)
      : dt.isRequired;
    return isRequiredForClient;
  });

  // Export client data to CSV
  const exportToCSV = () => {
    if (!client) return;

    // Client info
    const clientInfo = [
      ['Client Information'],
      ['Name', client.name],
      ['Email', client.email],
      ['Phone', client.phone || 'N/A'],
      ['Company', client.company || 'N/A'],
      ['ABN', client.abn || 'N/A'],
      ['Status', client.status],
      ['Risk Level', client.riskLevel || 'N/A'],
      ['Risk Score', client.riskScore?.toString() || 'N/A'],
      ['Intake Progress', `${client.intakeProgress}%`],
      ['Created', formatDate(client.createdAt)],
      ['Last Login', formatDate(client.lastLoginAt)],
      ['Submitted', formatDate(client.submittedAt)],
      ['Approved', formatDate(client.approvedAt)],
      ['Notes', client.notes || 'N/A'],
      [],
      ['Document Summary'],
      ['Total Required', requiredDocTypes.length.toString()],
      ['Uploaded', documents.length.toString()],
      ['Approved', approvedDocs.toString()],
      ['Rejected', rejectedDocs.toString()],
      ['Missing', missingDocTypes.length.toString()],
      [],
      ['Documents'],
      ['Document Type', 'File Name', 'Status', 'Upload Date', 'Validated By', 'Validation Date'],
    ];

    // Add document rows
    documents.forEach(doc => {
      const docType = documentTypes.find(dt => dt.id === doc.documentTypeId);
      clientInfo.push([
        docType?.name || 'Unknown',
        doc.originalName,
        doc.status,
        formatDate(doc.uploadedAt),
        doc.validatedBy || 'N/A',
        formatDate(doc.validatedAt),
      ]);
    });

    // Add extracted data section
    clientInfo.push([]);
    clientInfo.push(['Extracted Data']);

    documents.forEach(doc => {
      if (doc.extractedData && Object.keys(doc.extractedData).length > 0) {
        const docType = documentTypes.find(dt => dt.id === doc.documentTypeId);
        clientInfo.push([`--- ${docType?.name || 'Document'} ---`]);
        Object.entries(doc.extractedData).forEach(([key, value]) => {
          clientInfo.push([key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), String(value)]);
        });
        clientInfo.push([]);
      }
    });

    // Convert to CSV
    const csvContent = clientInfo.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${client.name.replace(/\s+/g, '_')}_intake_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('Client data exported to CSV');
  };

  // Copy client summary to clipboard
  const copyClientSummary = () => {
    if (!client) return;

    const summary = `
CLIENT INTAKE SUMMARY
=====================
Name: ${client.name}
Email: ${client.email}
Phone: ${client.phone || 'N/A'}
Company: ${client.company || 'N/A'}
ABN: ${client.abn || 'N/A'}

Status: ${client.status.toUpperCase()}
Risk Level: ${client.riskLevel?.toUpperCase() || 'N/A'}
Progress: ${client.intakeProgress}%

Documents: ${approvedDocs}/${requiredDocTypes.length} approved
Missing: ${missingDocTypes.map(d => d.name).join(', ') || 'None'}

Created: ${formatDate(client.createdAt)}
Last Login: ${formatDate(client.lastLoginAt)}
${client.notes ? `\nNotes: ${client.notes}` : ''}
    `.trim();

    navigator.clipboard.writeText(summary);
    toast.success('Summary copied to clipboard');
  };

  const openValidationDialog = (doc: IntakeClientDocument, action: 'approve' | 'reject') => {
    setSelectedDocument(doc);
    setValidationAction(action);
    setAdminNotes('');
    setRejectionReason('');
    setValidateDialogOpen(true);
  };

  const handleValidation = () => {
    if (!selectedDocument) return;
    validateMutation.mutate({
      documentId: selectedDocument.id,
      action: validationAction,
      adminNotes: adminNotes || undefined,
      rejectionReason: validationAction === 'reject' ? rejectionReason : undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-64 lg:col-span-1" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Client Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested client could not be found.</p>
        <Button onClick={() => navigate('/panel/intake/clients')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/panel/intake/clients')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                {isEditing ? 'Edit Client' : client.name}
              </h1>
              {!isEditing && getStatusBadge(client.status)}
              {!isEditing && getRiskBadge(client.riskLevel)}
            </div>
            <p className="text-muted-foreground">
              {isEditing ? client.name : (client.company || client.email)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={updateClientMutation.isPending || !formData.name.trim()}
              >
                {updateClientMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={copyClientSummary}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Summary
              </Button>
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => resendEmailMutation.mutate()}>
                <Mail className="mr-2 h-4 w-4" />
                Resend Email
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10">
                <FileText className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-sm text-muted-foreground">Documents Uploaded</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{approvedDocs}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documentsNeedingReview.length}</p>
                <p className="text-sm text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{missingDocTypes.length}</p>
                <p className="text-sm text-muted-foreground">Missing Required</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Client Info Sidebar */}
        <div className="space-y-6">
          {/* Client Details Card - Edit Form or View */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {isEditing ? 'Edit Client Information' : 'Client Information'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                /* Edit Form */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Client name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email (read-only)</Label>
                    <Input
                      id="email"
                      value={client.email}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Email cannot be changed after creation
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Phone number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Company name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="abn">ABN</Label>
                    <Input
                      id="abn"
                      value={formData.abn}
                      onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                      placeholder="Australian Business Number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="riskLevel">Risk Level</Label>
                    <Select
                      value={formData.riskLevel || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, riskLevel: value === 'none' ? '' : value as RiskLevel })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select risk level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Assessed</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Internal notes about this client..."
                      rows={4}
                    />
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a href={`mailto:${client.email}`} className="text-primary hover:underline">
                        {client.email}
                      </a>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${client.phone}`} className="hover:underline">
                          {client.phone}
                        </a>
                      </div>
                    )}
                    {client.company && (
                      <div className="flex items-center gap-3 text-sm">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{client.company}</span>
                      </div>
                    )}
                    {client.abn && (
                      <div className="flex items-center gap-3 text-sm">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span>ABN: {client.abn}</span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Intake Progress</span>
                      <span className="font-medium">{client.intakeProgress}%</span>
                    </div>
                    <Progress value={client.intakeProgress} className="h-2" />
                  </div>

                  <Separator />

                  {/* Dates */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Created:</span>
                      <span>{formatDate(client.createdAt)}</span>
                    </div>
                    {client.lastLoginAt && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Last Login:</span>
                        <span>{formatDate(client.lastLoginAt)}</span>
                      </div>
                    )}
                    {client.submittedAt && (
                      <div className="flex items-center gap-2">
                        <Send className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Submitted:</span>
                        <span>{formatDate(client.submittedAt)}</span>
                      </div>
                    )}
                    {client.approvedAt && (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Approved:</span>
                        <span>{formatDate(client.approvedAt)}</span>
                      </div>
                    )}
                  </div>

                  {/* Risk Assessment */}
                  {client.riskLevel && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Risk Assessment</span>
                        </div>
                        <div className="flex items-center justify-between">
                          {getRiskBadge(client.riskLevel)}
                          {client.riskScore && (
                            <span className="text-sm text-muted-foreground">
                              Score: {client.riskScore}/100
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Notes */}
                  {client.notes && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <span className="text-sm font-medium">Notes</span>
                        <p className="text-sm text-muted-foreground">{client.notes}</p>
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Status Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Manage Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Client Status</Label>
                <Select
                  value={client.status}
                  onValueChange={(value) => updateStatusMutation.mutate(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {client.status === 'under_review' && documentsNeedingReview.length === 0 && approvedDocs >= requiredDocTypes.length && (
                <Button
                  className="w-full"
                  onClick={() => updateStatusMutation.mutate('approved')}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Approve Client
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Missing Documents */}
          {missingDocTypes.length > 0 && (
            <Card className="border-amber-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-500">
                  <AlertTriangle className="h-4 w-4" />
                  Missing Documents ({missingDocTypes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {missingDocTypes.map((docType) => (
                    <li key={docType.id} className="flex items-center gap-2 text-sm">
                      <XCircle className="h-4 w-4 text-amber-500" />
                      <span>{docType.name}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Documents & Data */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="documents" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="extracted">Extracted Data</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
            </TabsList>

            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Uploaded Documents</CardTitle>
                      <CardDescription>
                        {documents.length} documents uploaded, {documentsNeedingReview.length} pending review
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No documents uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {documents.map((doc) => {
                        const docType = documentTypes.find(dt => dt.id === doc.documentTypeId);
                        return (
                          <Card key={doc.id} className={cn(
                            doc.status === 'rejected' && 'border-red-500/50',
                            doc.status === 'approved' && 'border-green-500/50'
                          )}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-medium">{docType?.name || 'Unknown Document'}</p>
                                    {getDocStatusBadge(doc.status)}
                                    {doc.extractionConfidence && (
                                      <Badge variant="outline" className="text-xs">
                                        {Math.round(doc.extractionConfidence * 100)}% confidence
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground truncate">
                                    {doc.originalName} ({formatFileSize(doc.fileSizeBytes)})
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Uploaded: {formatDate(doc.uploadedAt)}
                                    {doc.validatedAt && ` | Validated: ${formatDate(doc.validatedAt)}`}
                                  </p>
                                  {doc.rejectionReason && (
                                    <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      {doc.rejectionReason}
                                    </p>
                                  )}
                                  {doc.adminNotes && (
                                    <p className="text-sm text-muted-foreground mt-2 italic">
                                      Note: {doc.adminNotes}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadDocument(doc.id)}
                                    disabled={downloadingDocId === doc.id}
                                  >
                                    {downloadingDocId === doc.id ? (
                                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    ) : (
                                      <Download className="mr-1 h-3 w-3" />
                                    )}
                                    Download
                                  </Button>
                                  {/* Extract button - show for docs that can have text extracted */}
                                  {['uploaded', 'extracted', 'validated', 'approved'].includes(doc.status) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleExtractDocument(doc.id, doc.originalName)}
                                      disabled={extractingDocId === doc.id}
                                    >
                                      {extractingDocId === doc.id ? (
                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                      ) : (
                                        <ScanText className="mr-1 h-3 w-3" />
                                      )}
                                      Extract
                                    </Button>
                                  )}
                                  {/* View Extracted Data button - show for docs with extracted data */}
                                  {(doc.status === 'extracted' || doc.extractedData) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewExtractedData(doc)}
                                    >
                                      <FileSpreadsheet className="mr-1 h-3 w-3" />
                                      Data
                                    </Button>
                                  )}
                                  {['uploaded', 'processing', 'extracted', 'validated'].includes(doc.status) && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="default"
                                        className="bg-green-600 hover:bg-green-700"
                                        onClick={() => openValidationDialog(doc, 'approve')}
                                        disabled={validateMutation.isPending}
                                      >
                                        <CheckCheck className="mr-1 h-3 w-3" />
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => openValidationDialog(doc, 'reject')}
                                        disabled={validateMutation.isPending}
                                      >
                                        <X className="mr-1 h-3 w-3" />
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Extracted Data Tab */}
            <TabsContent value="extracted" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Extracted Information</CardTitle>
                  <CardDescription>
                    Data automatically extracted from uploaded documents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {documents.filter(d => d.extractedData && Object.keys(d.extractedData).length > 0).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No extracted data available yet</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {documents.map((doc) => {
                        if (!doc.extractedData || Object.keys(doc.extractedData).length === 0) return null;
                        const docType = documentTypes.find(dt => dt.id === doc.documentTypeId);

                        // Get the actual fields to display - handle nested structure
                        const extractedData = doc.extractedData;
                        let fieldsToDisplay: Record<string, unknown> = {};

                        if (extractedData.type === 'drivers_license' && extractedData.fields) {
                          // Driver's license structure: { type, fields: {...} }
                          fieldsToDisplay = extractedData.fields;
                        } else if (extractedData.type === 'generic_document') {
                          // Generic document structure: { type, text, keyValuePairs, tables }
                          if (extractedData.keyValuePairs && Object.keys(extractedData.keyValuePairs).length > 0) {
                            fieldsToDisplay = extractedData.keyValuePairs;
                          }
                        } else {
                          // Fallback: use extractedData directly if it's flat
                          fieldsToDisplay = extractedData;
                        }

                        // Filter out non-displayable fields
                        const displayEntries = Object.entries(fieldsToDisplay).filter(([key, value]) => {
                          // Skip rawText, type, and complex objects
                          if (key === 'rawText' || key === 'type' || key === 'tables') return false;
                          if (typeof value === 'object' && value !== null) return false;
                          return true;
                        });

                        if (displayEntries.length === 0) return null;

                        return (
                          <div key={doc.id} className="space-y-3">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-primary" />
                              <h4 className="font-medium">{docType?.name || 'Document'}</h4>
                              {getDocStatusBadge(doc.status)}
                              {extractedData.type && (
                                <Badge variant="outline" className="text-xs">
                                  {extractedData.type === 'drivers_license' ? "Driver's License" :
                                   extractedData.type === 'generic_document' ? 'Document' : extractedData.type}
                                </Badge>
                              )}
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-1/3">Field</TableHead>
                                  <TableHead>Extracted Value</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {displayEntries.map(([key, value]) => (
                                  <TableRow key={key}>
                                    <TableCell className="font-medium text-muted-foreground capitalize">
                                      {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                    </TableCell>
                                    <TableCell>
                                      {typeof value === 'boolean'
                                        ? value ? 'Yes' : 'No'
                                        : typeof value === 'number'
                                          ? value.toLocaleString()
                                          : String(value || '-')
                                      }
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Consolidated Client Data for Accounting */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Accounting Summary</CardTitle>
                  <CardDescription>
                    Consolidated data extracted for accounting purposes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Personal/Business Info */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        Identity & Business
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Full Name:</span>
                          <span className="font-medium">{client.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Company:</span>
                          <span className="font-medium">{client.company || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">ABN:</span>
                          <span className="font-medium">{client.abn || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary from Extracted Data */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        Financial Data
                      </h4>
                      <div className="space-y-2 text-sm">
                        {documents.map(doc => {
                          if (!doc.extractedData) return null;
                          const data = doc.extractedData;
                          return (
                            <>
                              {data.total_income && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Total Income:</span>
                                  <span className="font-medium">${Number(data.total_income).toLocaleString()}</span>
                                </div>
                              )}
                              {data.tax_payable && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Tax Payable:</span>
                                  <span className="font-medium">${Number(data.tax_payable).toLocaleString()}</span>
                                </div>
                              )}
                              {data.closing_balance && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Bank Balance:</span>
                                  <span className="font-medium">${Number(data.closing_balance).toLocaleString()}</span>
                                </div>
                              )}
                              {data.gst_collected && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">GST Collected:</span>
                                  <span className="font-medium">${Number(data.gst_collected).toLocaleString()}</span>
                                </div>
                              )}
                            </>
                          );
                        })}
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        Bank Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        {documents.map(doc => {
                          if (!doc.extractedData) return null;
                          const data = doc.extractedData;
                          return (
                            <>
                              {data.bsb && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">BSB:</span>
                                  <span className="font-medium">{String(data.bsb)}</span>
                                </div>
                              )}
                              {data.account_number && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Account:</span>
                                  <span className="font-medium">{String(data.account_number)}</span>
                                </div>
                              )}
                              {data.account_name && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Account Name:</span>
                                  <span className="font-medium">{String(data.account_name)}</span>
                                </div>
                              )}
                            </>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tax Info */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        Tax Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        {documents.map(doc => {
                          if (!doc.extractedData) return null;
                          const data = doc.extractedData;
                          return (
                            <>
                              {data.tfn && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">TFN:</span>
                                  <span className="font-medium">{String(data.tfn)}</span>
                                </div>
                              )}
                              {data.tax_year && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Tax Year:</span>
                                  <span className="font-medium">{String(data.tax_year)}</span>
                                </div>
                              )}
                              {data.gst_registered !== undefined && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">GST Registered:</span>
                                  <span className="font-medium">{data.gst_registered ? 'Yes' : 'No'}</span>
                                </div>
                              )}
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline" className="space-y-4">
              <ClientTimelineTab clientId={actualClientId!} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Validation Dialog */}
      <Dialog open={validateDialogOpen} onOpenChange={setValidateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={validationAction === 'approve' ? 'text-green-600' : 'text-red-600'}>
              {validationAction === 'approve' ? 'Approve Document' : 'Reject Document'}
            </DialogTitle>
            <DialogDescription>
              {validationAction === 'approve'
                ? 'Confirm that this document meets all requirements.'
                : 'Provide a reason for rejecting this document.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedDocument && (
              <div className="rounded-lg border p-3 bg-muted/50">
                <p className="font-medium">
                  {documentTypes.find(dt => dt.id === selectedDocument.documentTypeId)?.name}
                </p>
                <p className="text-sm text-muted-foreground">{selectedDocument.originalName}</p>
              </div>
            )}

            {validationAction === 'reject' && (
              <div className="space-y-2">
                <Label htmlFor="rejectionReason">Rejection Reason *</Label>
                <Textarea
                  id="rejectionReason"
                  placeholder="Explain why this document is being rejected..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="adminNotes">Admin Notes (optional)</Label>
              <Textarea
                id="adminNotes"
                placeholder="Add any internal notes..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={validationAction === 'approve' ? 'default' : 'destructive'}
              onClick={handleValidation}
              disabled={validateMutation.isPending || (validationAction === 'reject' && !rejectionReason)}
              className={validationAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {validateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {validationAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extracted Data Dialog */}
      <Dialog open={extractedDataDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEditingExtractedData(false);
          setEditedFields({});
        }
        setExtractedDataDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanText className="h-5 w-5" />
              {isEditingExtractedData ? 'Edit Extracted Data' : 'Extracted Data'}
            </DialogTitle>
            <DialogDescription>
              {selectedExtractedData?.documentName}
              {selectedExtractedData?.confidence && !isEditingExtractedData && (
                <Badge variant="outline" className="ml-2">
                  {Math.round(selectedExtractedData.confidence * 100)}% confidence
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedExtractedData?.extractedData && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {selectedExtractedData.extractedData.type === 'drivers_license' && selectedExtractedData.extractedData.fields && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Driver's License Information
                  </h4>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableBody>
                        {Object.entries(selectedExtractedData.extractedData.fields)
                          .filter(([key]) => key !== 'rawText') // Don't show rawText in the table
                          .map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell className="font-medium capitalize bg-muted/50 w-1/3">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </TableCell>
                            <TableCell>
                              {isEditingExtractedData ? (
                                <Input
                                  value={editedFields[key] ?? String(value)}
                                  onChange={(e) => setEditedFields(prev => ({
                                    ...prev,
                                    [key]: e.target.value
                                  }))}
                                  className="h-8"
                                />
                              ) : (
                                String(value)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {selectedExtractedData.extractedData.type === 'generic_document' && (
                <div className="space-y-4">
                  {selectedExtractedData.extractedData.keyValuePairs &&
                   Object.keys(selectedExtractedData.extractedData.keyValuePairs).length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                        Extracted Fields
                      </h4>
                      <div className="rounded-lg border overflow-hidden">
                        <Table>
                          <TableBody>
                            {Object.entries(selectedExtractedData.extractedData.keyValuePairs).map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell className="font-medium bg-muted/50 w-1/3">{key}</TableCell>
                                <TableCell>
                                  {isEditingExtractedData ? (
                                    <Input
                                      value={editedFields[key] ?? String(value)}
                                      onChange={(e) => setEditedFields(prev => ({
                                        ...prev,
                                        [key]: e.target.value
                                      }))}
                                      className="h-8"
                                    />
                                  ) : (
                                    String(value)
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {selectedExtractedData.extractedData.text && !isEditingExtractedData && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                        Extracted Text
                      </h4>
                      <div className="rounded-lg border p-3 bg-muted/50 max-h-48 overflow-y-auto">
                        <pre className="text-sm whitespace-pre-wrap font-mono">
                          {selectedExtractedData.extractedData.text}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Show raw text in a collapsible section when viewing driver's license */}
              {selectedExtractedData.extractedData.type === 'drivers_license' &&
               selectedExtractedData.extractedData.fields?.rawText && !isEditingExtractedData && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Raw Extracted Text
                  </h4>
                  <div className="rounded-lg border p-3 bg-muted/50 max-h-32 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground">
                      {selectedExtractedData.extractedData.fields.rawText}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {isEditingExtractedData ? (
              <>
                <Button variant="outline" onClick={handleCancelEditExtractedData}>
                  Cancel
                </Button>
                <Button onClick={handleSaveExtractedData} disabled={savingExtractedData}>
                  {savingExtractedData && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setExtractedDataDialogOpen(false)}>
                  Close
                </Button>
                <Button variant="outline" onClick={handleStartEditExtractedData}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
